import { ChevRightIcon, Modal, Text, UsersIcon } from '@viapeople/components'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { formatDateTime, formatValue } from '../../../utils/formatters'
import { trpc } from '../../../utils/trpc'
import type { UserHistory, UserHistoryChangeDetail, UserHistoryListInput, UserHistoryModalProps } from '../types'

const formatChangeData = (change: UserHistoryChangeDetail) => {
	const specialFields: Record<string, { normalizedField: string; formatValue: (value: string | boolean) => string }> = {
		active: {
			normalizedField: 'Status',
			formatValue: (value) => (value ? 'Active' : 'Inactive'),
		},
		deleted: {
			normalizedField: 'Visibility',
			formatValue: (value) => (value ? 'Archived' : 'Activated'),
		},
	}

	if (change.field in specialFields) {
		const formatter = specialFields[change.field]
		return {
			normalizedField: formatter.normalizedField,
			oldValue: formatter.formatValue(change.oldValue),
			newValue: formatter.formatValue(change.newValue),
		}
	}
	return {
		normalizedField: change.field
			.split(/(?=[A-Z])/)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' '),
		oldValue: formatValue(change.oldValue),
		newValue: formatValue(change.newValue),
	}
}

const columns: Column<UserHistory>[] = [
	{
		field: 'timestamp',
		label: 'Updated at',
		sortable: false,
		render: (item: UserHistory) => formatDateTime(item.timestamp),
		colSpan: 1.5,
	},
	{
		field: 'type',
		label: 'Type',
		sortable: false,
		render: (item: UserHistory) => (item.type === 'manual' ? 'Manual' : 'File Upload'),
	},
	{
		field: 'detail',
		label: 'Detail',
		sortable: false,
		colSpan: 2,
	},
	{
		field: 'detail',
		label: 'Modified by',
		sortable: false,
		colSpan: 1.5,
		render: (item: UserHistory) => `${item.modifiedByFirstName} ${item.modifiedByLastName}`,
	},
	{
		field: 'changedFields',
		label: 'Changed Fields',
		sortable: false,
		colSpan: 4,
		render: (item: UserHistory) => (
			<>
				{item.changedFields.map((change, index) => {
					const { normalizedField, oldValue, newValue } = formatChangeData(change)
					const key = `${change.field}-${index}`
					return (
						<div key={key} className='flex flex-col mb-2'>
							<Text variant='xsmall' weight='semibold'>
								{normalizedField}
							</Text>
							<div className='flex flex-row items-center gap-2 relative'>
								<Text variant='xsmall' className='text-error-600 capitalize max-w-[40%] truncate'>
									{oldValue}
								</Text>
								<ChevRightIcon size={16} className='fill-neutral-500' />
								<Text variant='xsmall' className='text-success-600 capitalize max-w-[40%] truncate'>
									{newValue}
								</Text>
							</div>
						</div>
					)
				})}
			</>
		),
	},
]

export const UserHistoryModal = ({ isOpen, onClose }: UserHistoryModalProps) => {
	const { userId } = useParams()

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='User History' fullContent className='md:w-300 max-w-[1110px] mx-12'>
			<GenericTable<UserHistory, UserHistoryListInput>
				columns={columns}
				isInternal
				className='min-h-[460px]'
				bodyClassName='max-h-[460px] overflow-y-auto'
				showSearch={false}
				queryOptionsGenerator={(options) =>
					trpc.admin.userHistory.list.queryOptions({
						...options,
						userId: userId as string,
					})
				}
				emptyState={{
					icon: UsersIcon,
					title: 'No user history found',
					description: 'No user history found for this user',
					actionLabel: 'Close',
					onAction: onClose,
					className: 'flex-grow',
				}}
			/>
		</Modal>
	)
}

export default UserHistoryModal
